import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchNotifications, markAllNotificationsRead, markNotificationRead } from '../api/notifications';
import DashboardShell from '../components/ui/DashboardShell';
import PaginatedFooter from '../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../constants/pagination';
import { ListSkeleton } from '../components/ui/Skeleton';
import { sanitizeText } from '../utils/sanitize';
import { timeAgo } from '../utils/timeAgo';
import { notificationIcon } from '../components/notifications/notificationIcons';

const FILTER_IDS = ['all', 'unread', 'APPOINTMENTS', 'ORDERS'];

const NotificationsPage = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const params = {
    page,
    limit,
    ...(filter === 'unread' ? { unreadOnly: 'true' } : {}),
    ...(filter && !['unread', ''].includes(filter) ? { type: filter } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', params],
    queryFn: () => fetchNotifications(params),
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const markOne = useMutation({ mutationFn: markNotificationRead });

  const items = data?.data || [];
  const pagination = data?.pagination;

  const openNotification = async (n) => {
    if (!n.isRead) {
      await markOne.mutateAsync(n._id);
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    }
    if (n.link) navigate(n.link);
  };

  return (
    <DashboardShell title={t('notifications.title')} subtitle={t('notifications.subtitle')}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {FILTER_IDS.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setFilter(id === 'all' ? '' : id);
                setPage(1);
              }}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                (id === 'all' ? filter === '' : filter === id)
                  ? 'bg-glow-600/20 text-glow-300'
                  : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'
              }`}
            >
              {t(`notifications.filters.${id}`)}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => markAll.mutate()}
          className="text-sm text-glow-400 hover:text-glow-300"
        >
          {t('notifications.markAllRead')}
        </button>
      </div>

      {isLoading && <ListSkeleton rows={6} />}

      {!isLoading && items.length === 0 && (
        <p className="py-12 text-center text-zinc-600 dark:text-zinc-500">{t('notifications.empty')}</p>
      )}

      <div className="space-y-2">
        {items.map((n) => (
          <button
            key={n._id}
            type="button"
            onClick={() => openNotification(n)}
            className={`flex w-full gap-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 text-left transition hover:border-zinc-300 dark:border-zinc-700 ${
              !n.isRead ? 'border-l-4 border-l-blue-500' : ''
            }`}
          >
            <span className="text-2xl">{notificationIcon(n.type)}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-zinc-900 dark:text-zinc-100">{sanitizeText(n.title)}</p>
                <span className="shrink-0 text-xs text-zinc-600 dark:text-zinc-500">{timeAgo(n.createdAt)}</span>
              </div>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{sanitizeText(n.message)}</p>
            </div>
          </button>
        ))}
      </div>

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={limit}
        onPageSizeChange={(n) => { setLimit(n); setPage(1); }}
      />
    </DashboardShell>
  );
};

export default NotificationsPage;
